/**
 * Author: Romain Hapi Sr.
 * Version: 2.0
 * Date: 2026-09-10
 * Project: Prenim Digital
 * Copyright: Prenim UK
 */
/**
 * En-tete du site Tchad.
 *
 * En `fixed` et transparent en haut de page, il se pose sur le hero sombre : les
 * libelles sont alors blancs. Des que la page defile — ou que le menu mobile
 * s'ouvre, qui deroulerait sinon sur du transparent — le fond devient opaque et
 * les libelles repassent en ardoise.
 *
 * La page courante est marquee d'un filet or sous le libelle, et non d'une
 * pastille pleine : une pastille de couleur pesait autant qu'un bouton d'action
 * et brouillait la hierarchie.
 */
import Flag from '../../components/Flag';
import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Globe, Check } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import { SHELL } from '../theme';
import Wordmark, { lockupHeightEm } from '../../components/Wordmark';

const LANGUAGES = [
  { code: 'fr', label: 'Français', flag: 'fr' },
  { code: 'en', label: 'English', flag: 'gb' },
  { code: 'ar', label: 'العربية', flag: 'sa' },
];

export const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const { language, changeLanguage, t } = useLanguage();
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 20);
    handleScroll();
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Le menu mobile se referme au changement de page : sans cela, il restait
  // ouvert par-dessus la page suivante quand la navigation venait d'ailleurs.
  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  const navLinks = [
    { path: '/tchad', label: t.nav.home },
    { path: '/tchad/services', label: t.nav.services },
    { path: '/tchad/about', label: t.nav.about },
    { path: '/tchad/projects', label: t.nav.projects },
    { path: '/tchad/careers', label: t.nav.careers },
    { path: '/tchad/contact', label: t.nav.contact },
  ];

  // Tant que le menu mobile est deploye, l'en-tete prend son fond opaque :
  // deroule sur un hero sombre, il serait illisible en transparent.
  const solid = isScrolled || isMobileMenuOpen;

  return (
    <header
      data-testid="tchad-header"
      className={`fixed inset-x-0 top-0 z-50 transition-colors duration-300 ${
        solid
          ? 'border-b border-slate-200 bg-white/95 backdrop-blur-md'
          : 'border-b border-transparent bg-transparent'
      }`}
    >
      <nav className={SHELL}>
        <div className="flex h-20 items-center justify-between gap-4">
          <Link
            to="/tchad"
            data-testid="tchad-header-logo"
            className="flex shrink-0 items-center gap-3 text-[1.5rem] sm:text-[1.8rem] lg:text-[2.05rem]"
          >
            <img
              src="/images/logo-prenim-tchad.webp"
              alt=""
              aria-hidden="true"
              width="44"
              height="40"
              style={{ height: `${lockupHeightEm(0.4)}em` }}
              className="w-auto shrink-0 object-contain"
            />
            {/* « Prenim Tchad » est bien plus large que « Prenim » : sa
                signature peut donc etre composee a 0.40em du nom, la ou le site
                UK doit descendre a 0.22em pour tenir dans six lettres. */}
            <Wordmark
              name="Prenim Tchad"
              tagline="Transformation Digitale"
              ratio={0.4}
              testId="tchad-header-wordmark"
              nameClassName={`transition-colors ${solid ? 'text-slate-950' : 'text-white'}`}
              taglineClassName={`transition-colors ${solid ? 'text-amber-600' : 'text-amber-300'}`}
            />
          </Link>

          {/* Navigation : la page courante est marquee par un filet or sous le
              libelle, et non par une pastille pleine. Une pastille de couleur
              pesait autant qu'un bouton d'action et brouillait la hierarchie. */}
          <div className="hidden items-center gap-1 lg:flex">
            {navLinks.map((link) => {
              const active = location.pathname === link.path;
              return (
                <Link
                  key={link.path}
                  to={link.path}
                  data-testid={`tchad-nav-${link.path.split('/').pop() || 'home'}`}
                  aria-current={active ? 'page' : undefined}
                  className={`relative px-3 py-2 text-sm font-medium transition-colors ${
                    solid
                      ? active
                        ? 'text-slate-950'
                        : 'text-slate-600 hover:text-slate-950'
                      : active
                      ? 'text-white'
                      : 'text-white/75 hover:text-white'
                  }`}
                >
                  {link.label}
                  {active ? (
                    <span
                      aria-hidden="true"
                      className="absolute inset-x-3 -bottom-0.5 h-0.5 bg-amber-500"
                    />
                  ) : null}
                </Link>
              );
            })}
          </div>

          <div className="flex items-center gap-2">
            <Link
              to="/"
              data-testid="tchad-header-uk"
              className={`hidden items-center gap-2 rounded-full border-2 px-4 py-2.5 text-sm font-semibold transition-colors lg:inline-flex ${
                solid
                  ? 'border-slate-300 text-slate-700 hover:border-slate-950 hover:text-slate-950'
                  : 'border-white/40 text-white hover:border-white hover:bg-white/10'
              }`}
            >
              <Flag code="gb" />
              Prenim UK
            </Link>

            <DropdownMenu>
              <DropdownMenuTrigger
                data-testid="tchad-lang-trigger"
                className={`inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-sm font-medium transition-colors ${
                  solid
                    ? 'border-slate-300 text-slate-700 hover:bg-slate-100'
                    : 'border-white/40 text-white hover:bg-white/10'
                }`}
              >
                <Globe className="h-4 w-4" />
                <span className="uppercase">{language}</span>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="min-w-[180px]">
                {LANGUAGES.map((lang) => (
                  <DropdownMenuItem
                    key={lang.code}
                    data-testid={`tchad-lang-${lang.code}`}
                    onClick={() => changeLanguage(lang.code)}
                    className="flex cursor-pointer items-center justify-between gap-3"
                  >
                    <span className="flex items-center gap-2">
                      <Flag code={lang.flag} />
                      <span>{lang.label}</span>
                    </span>
                    {language === lang.code ? (
                      <Check className="h-4 w-4 text-amber-600" />
                    ) : null}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            <button
              type="button"
              data-testid="tchad-menu-toggle"
              onClick={() => setIsMobileMenuOpen((open) => !open)}
              aria-expanded={isMobileMenuOpen}
              aria-label={isMobileMenuOpen ? 'Fermer le menu' : 'Ouvrir le menu'}
              className={`rounded-md p-2 transition-colors lg:hidden ${
                solid ? 'text-slate-700 hover:bg-slate-100' : 'text-white hover:bg-white/10'
              }`}
            >
              {isMobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>

        {isMobileMenuOpen ? (
          <div data-testid="tchad-mobile-menu" className="border-t border-slate-200 py-3 lg:hidden">
            {navLinks.map((link) => {
              const active = location.pathname === link.path;
              return (
                <Link
                  key={link.path}
                  to={link.path}
                  onClick={() => setIsMobileMenuOpen(false)}
                  aria-current={active ? 'page' : undefined}
                  className={`block py-3 text-sm font-medium transition-colors ${
                    active
                      ? 'border-s-2 border-amber-500 ps-3 text-slate-950'
                      : 'ps-3 text-slate-600 hover:text-slate-950'
                  }`}
                >
                  {link.label}
                </Link>
              );
            })}
            <Link
              to="/"
              onClick={() => setIsMobileMenuOpen(false)}
              className="mt-2 flex items-center gap-2 border-t border-slate-200 ps-3 pt-4 text-sm font-semibold text-slate-700 hover:text-slate-950"
            >
              <Flag code="gb" />
              Prenim UK
            </Link>
          </div>
        ) : null}
      </nav>
    </header>
  );
};
